import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MainLayout } from '@/components/layout/MainLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { UserMenu } from '@/components/common/UserMenu';
import { PillTabs } from '@/components/common/PillTabs';
import { MetricCard } from '@/components/dashboard/CardsResumo';
import { TabelaOS } from '@/components/ordens/TabelaOS';
import {
  useOrdensServico,
  useOSDisponiveis,
} from '@/hooks/useOrdensServico';
import { useMeuCadastroPrestador } from '@/hooks/usePrestadores';
import { useRealtimeOSDisponiveis } from '@/hooks/useRealtimeOS';
import { useAuthContext } from '@/contexts/AuthContext';
import type { OrdemServico, StatusOrdemServico } from '@/types/ordemServico';
import { Loader2, AlertCircle, Info } from 'lucide-react';

type TabServicos = 'disponiveis' | 'aceitas' | 'andamento' | 'concluidas';

export default function MeusServicos() {
  const { t } = useTranslation();
  const { profile } = useAuthContext();
  const { data: meuCadastro, isLoading: loadingCadastro } = useMeuCadastroPrestador();

  const [tabServicos, setTabServicos] = useState<TabServicos>('disponiveis');

  const servicosOferecidos = meuCadastro?.servicos_oferecidos as string[] | undefined;
  const { data: osDisponiveis, isLoading: loadingDisponiveis } =
    useOSDisponiveis(servicosOferecidos);

  useRealtimeOSDisponiveis({ enabled: !!meuCadastro });

  const filtrosAceitas = meuCadastro
    ? { prestador_id: meuCadastro.id, status: ['aceita'] as StatusOrdemServico[] }
    : undefined;
  const filtrosEmAndamento = meuCadastro
    ? { prestador_id: meuCadastro.id, status: ['em_execucao'] as StatusOrdemServico[] }
    : undefined;
  const filtrosConcluidas = meuCadastro
    ? { prestador_id: meuCadastro.id, status: ['concluida'] as StatusOrdemServico[] }
    : undefined;

  const { data: osAceitas, isLoading: loadingAceitas } = useOrdensServico(filtrosAceitas);
  const { data: osEmAndamento, isLoading: loadingAndamento } = useOrdensServico(filtrosEmAndamento);
  const { data: osConcluidas, isLoading: loadingConcluidas } = useOrdensServico(filtrosConcluidas);

  if (loadingCadastro) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[50vh]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </MainLayout>
    );
  }

  if (!meuCadastro) {
    return (
      <MainLayout>
        <div className="p-6 text-center">
          <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold">{t('myServices.registrationNotFound')}</h2>
          <p className="text-muted-foreground mt-2">
            {t('myServices.registrationNotFoundDescription')}
          </p>
        </div>
      </MainLayout>
    );
  }

  const subTabs: { value: TabServicos; label: string; count?: number }[] = [
    {
      value: 'disponiveis',
      label: t('myServices.serviceTabs.available'),
      count: osDisponiveis?.length,
    },
    { value: 'aceitas', label: t('myServices.serviceTabs.accepted'), count: osAceitas?.length },
    {
      value: 'andamento',
      label: t('myServices.serviceTabs.inProgress'),
      count: osEmAndamento?.length,
    },
    {
      value: 'concluidas',
      label: t('myServices.serviceTabs.completed'),
      count: osConcluidas?.length,
    },
  ];

  return (
    <MainLayout>
      <div className="p-6 flex flex-col gap-5">
        <PageHeader
          titulo={t('myServices.title')}
          acoes={
            <>
              <UserMenu />
            </>
          }
        />

        {profile?.nome && (
          <p className="text-sm text-muted-foreground -mt-3">
            {t('myServices.greeting', { name: profile.nome })}
          </p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            titulo={t('myServices.serviceTabs.available')}
            valor={loadingDisponiveis ? '…' : osDisponiveis?.length || 0}
          />
          <MetricCard
            titulo={t('myServices.serviceTabs.accepted')}
            valor={loadingAceitas ? '…' : osAceitas?.length || 0}
          />
          <MetricCard
            titulo={t('myServices.serviceTabs.inProgress')}
            valor={loadingAndamento ? '…' : osEmAndamento?.length || 0}
          />
          <MetricCard
            titulo={t('myServices.serviceTabs.completed')}
            valor={loadingConcluidas ? '…' : osConcluidas?.length || 0}
          />
        </div>

        <PillTabs<TabServicos>
          value={tabServicos}
          options={subTabs}
          onChange={setTabServicos}
        />

        {tabServicos === 'disponiveis' && (
          <ListaOS
            loading={loadingDisponiveis}
            lista={osDisponiveis}
            empty={t('myServices.empty.noAvailable')}
            hint={t('orders.sheet.reviewBeforeAccept')}
          />
        )}

        {tabServicos === 'aceitas' && (
          <ListaOS
            loading={loadingAceitas}
            lista={osAceitas}
            empty={t('myServices.empty.noAccepted')}
          />
        )}

        {tabServicos === 'andamento' && (
          <ListaOS
            loading={loadingAndamento}
            lista={osEmAndamento}
            empty={t('myServices.empty.noInProgress')}
          />
        )}

        {tabServicos === 'concluidas' && (
          <ListaOS
            loading={loadingConcluidas}
            lista={osConcluidas}
            empty={t('myServices.empty.noCompleted')}
          />
        )}
      </div>
    </MainLayout>
  );
}

interface ListaOSProps {
  loading: boolean;
  lista?: OrdemServico[];
  empty: string;
  hint?: string;
}
function ListaOS({ loading, lista, empty, hint }: ListaOSProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!lista || lista.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground text-sm">{empty}</div>
    );
  }
  return (
    <>
      {hint && (
        <div className="ow-card">
          <div className="p-3 flex items-start gap-2 text-sm">
            <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-foreground" />
            <span>{hint}</span>
          </div>
        </div>
      )}
      <TabelaOS lista={lista} />
    </>
  );
}
